import { useContext, useEffect } from "react"
import { API, Auth } from "aws-amplify"
import { MarginsDiscountsContext } from "context/margin/MarginsDiscountsContext"
import {
  LOADING,
  SET_ACCORDION_SELECTED,
  SET_ADD_EXTRA_DISCOUNT_PERCENTAGE,
  SET_ADD_EXTRA_PERCENTAGE,
  SET_COST_REDUCTION,
  SET_DISCOUNT_ACCORDION_SELECTED,
  SET_GENERAL_DISCOUNT,
  SET_GENERAL_EXPENSES,
  SET_INDIRECT_EXPENSES,
  SET_INDUSTRIAL_PROFIT,
  SET_INITIAL_MARGIN_DATA,
  SET_IS_CLIENT_PRICE,
  SET_OVERRIDE_DISCOUNTS,
  SET_OVERWRITE_COST_REDUCTION,
  SET_OVERWRITE_MARGIN,
  SET_OVERWRITE_MARGIN_EXTRA,
  SET_TAB,
  SET_TOTAL_MARGIN,
} from "context/types"
import { EXTRA, GENERAL } from "context/margin/MarginsDiscountsReducer"
import { useBudget2023Context } from "context/budget2023/Budget2023Context"
import {
  CLIENT,
  NOT_SET,
} from "components/Budgeting2023/options/margin/FormulasTab"

const SERVICE_CORE_BASE = "service-core-base"
export const useMarginsDiscounts = () => {
  const { getBudgetTree, marginModal } = useBudget2023Context()

  const context = useContext(MarginsDiscountsContext)
  if (!context) {
    throw new Error(
      "useMarginsDiscounts must be used within a MarginsDiscountsProvider",
    )
  }

  const { state, dispatch } = context

  useEffect(() => {
    const total =
      state.margins.generalExpenses +
      state.margins.indirectExpenses +
      state.margins.industrialProfit

    dispatch({ type: SET_TOTAL_MARGIN, payload: total })
  }, [
    state.margins.generalExpenses,
    state.margins.indirectExpenses,
    state.margins.industrialProfit,
  ])

  const buildMarginsDiscountsPayload = () => {
    const marginType = state.margins.accordionSelected
    const discountType = state.discounts.accordionSelected

    let margins_mode
    let margins
    let hard

    if (marginType === GENERAL) {
      margins_mode = "SET"
      margins = {
        costeGeneral: parseFloat(state.margins.generalExpenses),
        costeIndirecto: parseFloat(state.margins.indirectExpenses),
        industrialProfit: parseFloat(state.margins.industrialProfit),
      }
      hard = state.margins.overwrite
    } else if (marginType === EXTRA) {
      margins_mode = "SUM"
      margins = {
        costeGeneral: parseFloat(state.margins.addExtraPercentage),
        costeIndirecto: 0,
        industrialProfit: 0,
      }
      hard = state.margins.overwriteExtra
    }

    let discount
    let discount_mode
    if (discountType === GENERAL) {
      discount = parseFloat(state.discounts.generalDiscount)
      discount_mode = "SET"
    } else if (discountType === EXTRA) {
      discount_mode = "SUM"

      discount = parseFloat(state.discounts.addExtraDiscountPercentage)
    }

    const marginFormula =
      state.formulas.isClientPrice === true ? "CLIENT" : "COST"

    return {
      hard,
      margins_mode,
      margins,
      discount_mode,
      discount,
      marginFormula: marginFormula,
      costReduction: state.bajas.costReduction,
      overrideCostReduction: state.bajas.costReductionOverwrite,
    }
  }

  const saveConfig = async projectId => {
    try {
      dispatch({ type: LOADING, payload: true })
      let payload = {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`,
          "Access-Control-Allow-Origin": "*",
        },
        body: buildMarginsDiscountsPayload(),
        queryStringParameters: {
          projectId: projectId,
        },
      }

      const path = `/projects/discount_margins`

      await API.put(SERVICE_CORE_BASE, path, payload)

      // No cerramos el modal aquí, lo haremos desde el componente que llama a esta función
      getBudgetTree(projectId, null)

      setCostReduction(0) // Reset cost reduction after saving
      setTab(0) // Reset to the first tab after saving

      return true
    } catch (error) {
      console.error("Error saving margins and discounts data:", error)
      return false
    } finally {
      dispatch({ type: LOADING, payload: false })
    }
  }

  const loadInitialData = () => {
    dispatch({
      type: SET_INITIAL_MARGIN_DATA,
      payload: {
        margins: {
          generalExpenses: marginModal?.generalExpenses || 0,
          indirectExpenses: marginModal?.indirectExpenses || 0,
          industrialProfit: marginModal?.industrialProfit || 0,
        },
        discounts: {
          generalDiscount: marginModal?.discount || 0,
        },
        formulas: {
          isClientPrice:
            marginModal?.formula === CLIENT || marginModal?.formula === NOT_SET,
        },
        bajas: {
          costReduction: 0,
        },
      },
    })
  }

  const setTab = tab => {
    dispatch({ type: SET_TAB, payload: tab })
  }
  const setAccordionSelected = accordion => {
    dispatch({ type: SET_ACCORDION_SELECTED, payload: accordion })
  }

  const setDiscountAccordionSelected = accordion => {
    dispatch({ type: SET_DISCOUNT_ACCORDION_SELECTED, payload: accordion })
  }

  const setOverwrite = value => {
    dispatch({ type: SET_OVERWRITE_MARGIN, payload: value })
  }
  const setOverwriteExtra = value => {
    dispatch({ type: SET_OVERWRITE_MARGIN_EXTRA, payload: value })
  }

  const setGeneralExpenses = value => {
    if (value === "") {
      value = 0
    }
    dispatch({ type: SET_GENERAL_EXPENSES, payload: value })
  }

  const setIndirectExpenses = value => {
    if (value === "") {
      value = 0
    }
    dispatch({ type: SET_INDIRECT_EXPENSES, payload: value })
  }

  const setIndustrialProfit = value => {
    if (value === "") {
      value = 0
    }
    dispatch({ type: SET_INDUSTRIAL_PROFIT, payload: value })
  }

  const setAddExtraPercentage = value => {
    dispatch({ type: SET_ADD_EXTRA_PERCENTAGE, payload: value })
  }

  const setGeneralDiscount = value => {
    dispatch({ type: SET_GENERAL_DISCOUNT, payload: value })
  }

  const setAddExtraDiscountPercentage = value => {
    dispatch({ type: SET_ADD_EXTRA_DISCOUNT_PERCENTAGE, payload: value })
  }

  const setOverrideDiscounts = value => {
    dispatch({ type: SET_OVERRIDE_DISCOUNTS, payload: value })
  }

  const setIsClientPrice = value => {
    dispatch({ type: SET_IS_CLIENT_PRICE, payload: value })
  }
  const setCostReduction = value => {
    dispatch({ type: SET_COST_REDUCTION, payload: value })
  }

  const setCostReductionOverwrite = value => {
    dispatch({ type: SET_OVERWRITE_COST_REDUCTION, payload: value })
  }

  return {
    state,
    marginModal,
    loadInitialData,
    setTab,
    saveConfig,
    setGeneralExpenses,
    setOverwrite,
    setOverwriteExtra,
    setIndirectExpenses,
    setIndustrialProfit,
    setAddExtraPercentage,
    setGeneralDiscount,
    setAddExtraDiscountPercentage,
    setOverrideDiscounts,
    setIsClientPrice,
    setAccordionSelected,
    setDiscountAccordionSelected,
    setCostReduction,
    setCostReductionOverwrite,
  }
}
